import {handleActions} from 'redux-actions';
import {Record, List} from 'immutable';

import * as BillPayerActions from 'web-app/react/entities/bill-payers/actions';

import * as Actions from './actions';

const AddBillPayerModal = Record({
    isVisible: false,
    persist: false,
    childId: undefined,
    institutionId: undefined,
    editingBillPayer: null,
    editingSplitConfiguration: null,
    searchResults: List(),
    saving: false,
    searching: false,
    candidateBillPayers: List(),
    fethcingCandidateBillPayers: false,

    // Passed on to the bill payer form if provided
    // Expected format:
    // {
    //     mandatoryFields: ['name', 'street'],
    //     regexValidations: {
    //         phone: {
    //             regex: "^\d{9}\d{2}?$",
    //             errorMessage: "It is not correct",
    //         }
    //     }
    // }
    validationPayload: null,
});

export default handleActions(
    {
        [Actions.show.type]: (state, {payload}) =>
            state.merge({
                isVisible: true,
                persist: payload.persist,
                childId: payload.childId,
                institutionId: payload.institutionId,
                editingBillPayer: payload.editingBillPayer,
                fethcingCandidateBillPayers: Boolean(payload.childId),
                validationPayload: payload.validationPayload,
            }),
        [Actions.showWithExistingSplit.type]: (state, {payload}) =>
            state.merge({
                isVisible: true,
                persist: payload.persist,
                childId: payload.childId,
                institutionId: payload.institutionId,
                editingBillPayer: payload.editingBillPayer,
                editingSplitConfiguration: payload.editingSplitConfiguration,
                fethcingCandidateBillPayers: Boolean(payload.childId),
            }),
        [Actions.hide.type]: () => new AddBillPayerModal(),
        [Actions.search.type]: state =>
            state.merge({
                searching: true,
            }),
        [Actions.searchSuccess.type]: (state, {payload}) =>
            state.merge({
                searchResults:
                    payload.initial && !hasSuggestions(payload.billPayers, state.editingSplitConfiguration)
                        ? List()
                        : payload.billPayers,
                searching: false,
            }),
        [Actions.searchFailed.type]: state =>
            state.merge({
                searching: false,
            }),
        [Actions.searchAborted.type]: state =>
            state.merge({
                searching: false,
            }),
        [Actions.setCandidateBillPayers.type]: (state, {payload}) =>
            state.merge({
                candidateBillPayers: payload.candidateBillPayers,
                fethcingCandidateBillPayers: false,
            }),
        [Actions.addBillPayerToChild.type]: state => state.merge({saving: true}),
        [Actions.addBillPayerToChildSuccess.type]: () => new AddBillPayerModal(),
        [Actions.addBillPayerToChildFailed.type]: state => state.merge({saving: false}),
        [Actions.reviveBillPayerAndAddToChild.type]: state => state.merge({saving: true}),
        [Actions.reviveBillPayerAndAddToChildSuccess.type]: () => new AddBillPayerModal(),
        [Actions.reviveBillPayerAndAddToChildFailed.type]: state => state.merge({saving: false}),
        [BillPayerActions.addBillPayerToChild.type]: state => state.merge({saving: true}),
        [BillPayerActions.addBillPayerToChildSuccess.type]: () => new AddBillPayerModal(),
        [BillPayerActions.addBillPayerToChildFailed.type]: state => state.merge({saving: false}),
        [BillPayerActions.fetchSensitiveInformationSuccess.type]: (state, {payload}) => {
            if (state.editingBillPayer?.billPayerId === payload.billPayerId) {
                return state.merge({
                    editingBillPayer: state.editingBillPayer.merge({
                        sensitive: payload.sensitiveInformation,
                    }),
                });
            } else {
                return state.merge({
                    searchResults: insertSensitiveInformation(
                        state.searchResults,
                        payload.billPayerId,
                        payload.sensitiveInformation,
                    ),
                    candidateBillPayers: insertSensitiveInformation(
                        state.candidateBillPayers,
                        payload.billPayerId,
                        payload.sensitiveInformation,
                    ),
                });
            }
        },
        [Actions.saveBillPayer.type]: state => state.merge({saving: true}),
        [Actions.saveBillPayerSuccess.type]: () => new AddBillPayerModal(),
        [Actions.saveBillPayerFailed.type]: state => state.merge({saving: false}),
        [Actions.discontinueBillPayer.type]: state => state.merge({saving: true}),
        [Actions.discontinueBillPayerSuccess.type]: () => new AddBillPayerModal(),
        [Actions.discontinueBillPayerFailed.type]: state => state.merge({saving: false}),
        [Actions.reviveBillPayer.type]: state => state.merge({saving: true}),
        [Actions.reviveBillPayerSuccess.type]: () => new AddBillPayerModal(),
        [Actions.reviveBillPayerFailed.type]: state => state.merge({saving: false}),
    },
    new AddBillPayerModal(),
);

const hasSuggestions = (billPayers, editingSplitConfiguration) => {
    const suggestions = billPayers
        ? billPayers.filter(billPayer => {
              return (
                  !editingSplitConfiguration ||
                  !editingSplitConfiguration.shares ||
                  !editingSplitConfiguration.shares.find(
                      splitBillPayer => splitBillPayer.billPayerId === billPayer.searchId,
                  )
              );
          })
        : List();

    return suggestions.size > 0;
};

const insertSensitiveInformation = (billPayerList, billPayerId, sensitiveInformation) => {
    return billPayerList.map(result => {
        if (result.searchId === billPayerId) {
            return result.merge({
                sensitive: sensitiveInformation,
            });
        } else {
            return result;
        }
    });
};
